import { useEffect, useState, useRef } from "react";

export default function OperationSelector({ order, onSelect, selected, workCenter, showAllOperations = false }) {
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Always filter to order-specific operations only (no toggle)

  // Track the selected operation code
  const [selectedOpCode, setSelectedOpCode] = useState("");

  // Operation codes in the current order, grouped by work center (fetched from API)
  // Format: { "ASSEMBLY": ["CUT", "GEN ASSEMBLY"], "OPTI": ["LAMINATE"], ... }
  const [orderOperationsByWc, setOrderOperationsByWc] = useState({});
  const [loadingOrderOps, setLoadingOrderOps] = useState(false);

  // Track retry attempts for token availability
  const retryCountRef = useRef(0);
  const maxRetries = 10;
  const retryDelayMs = 500;

  //
  // LOAD OPERATIONS - retries if token not yet available
  //
  useEffect(() => {
    const controller = new AbortController();
    let retryTimeout = null;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const token = sessionStorage.getItem("msal.backendToken");
        if (!token) {
          // Token not yet available - retry after delay
          if (retryCountRef.current < maxRetries) {
            retryCountRef.current += 1;
            console.log(`⏳ Waiting for backend token... (attempt ${retryCountRef.current}/${maxRetries})`);
            setLoading(false);
            retryTimeout = setTimeout(load, retryDelayMs);
            return;
          }
          // Max retries reached
          setError("No backend token available. Please refresh the page.");
          setLoading(false);
          return;
        }

        // Reset retry count on success
        retryCountRef.current = 0;

        const response = await fetch("/api/operations", {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch operations: ${response.status}`);
        }

        const data = await response.json();
        const ops = Array.isArray(data.operations) ? data.operations : [];
        console.log(`✅ /api/operations returned ${ops.length} operations`, data);
        setOperations(ops);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error loading operations:", err);
          setError("Unable to load operations.");
        }
      } finally {
        setLoading(false);
      }
    }

    load();
    return () => {
      controller.abort();
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, []);

  //
  // SYNC ROUTING → OPERATION SELECTOR
  //
  useEffect(() => {
    if (!selected) {
      setSelectedOpCode("");
      return;
    }

    const match = operations.find(
      (o) => o.operationCode === selected.operationCode
    );

    if (match) {
      setSelectedOpCode(match.operationCode);
    } else {
      setSelectedOpCode("");
    }
  }, [selected, operations]);

  //
  // FETCH OPERATIONS FOR THE CURRENT ORDER (for filtering)
  //
  useEffect(() => {
    if (!order?.orderNumber) {
      setOrderOperationsByWc({});
      return;
    }

    const controller = new AbortController();

    async function fetchOrderOperations() {
      setLoadingOrderOps(true);
      try {
        const token = sessionStorage.getItem("msal.backendToken");
        if (!token) return;

        const response = await fetch(
          `/api/orders/${encodeURIComponent(order.orderNumber)}/operations`,
          {
            signal: controller.signal,
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) {
          console.warn("Failed to fetch order operations:", response.status);
          return;
        }

        const data = await response.json();
        // Store operations grouped by work center
        const opsByWc = data.operationsByWorkCenter || {};
        setOrderOperationsByWc(opsByWc);

        const totalOps = Object.values(opsByWc).flat().length;
        const wcCount = Object.keys(opsByWc).length;
        console.log(`📋 Order ${order.orderNumber}: ${totalOps} operations across ${wcCount} work centers`, opsByWc);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error fetching order operations:", err);
        }
      } finally {
        setLoadingOrderOps(false);
      }
    }

    fetchOrderOperations();
    return () => controller.abort();
  }, [order?.orderNumber]);

  //
  // FILTER OPERATIONS BY WORK CENTER AND SORT BY DISPLAY ORDER
  // Special handling for combined work centers (ASSEMBLY_OPTI, 3DMODEL_PAINT)
  // PRINTING is considered part of Production Art (PA)
  //
  const effectiveWorkCenters = workCenter === "ASSEMBLY_OPTI"
    ? ["ASSEMBLY", "OPTI"]
    : workCenter === "3DMODEL_PAINT"
    ? ["3D MODEL", "PAINT"]
    : workCenter === "PA"
    ? ["PA", "PRINTING"]
    : workCenter ? [workCenter] : [];

  const baseFilteredOps = effectiveWorkCenters.length > 0
    ? operations
        .filter((o) => effectiveWorkCenters.some((wc) => o.workCenters?.includes(wc)))
        .sort((a, b) => {
          // For combined work centers, use primary display order if available, then fallback
          let primaryWc = workCenter;
          let fallbackWc = workCenter;
          if (workCenter === "ASSEMBLY_OPTI") {
            primaryWc = "ASSEMBLY";
            fallbackWc = "OPTI";
          } else if (workCenter === "3DMODEL_PAINT") {
            primaryWc = "3D MODEL";
            fallbackWc = "PAINT";
          }
          const orderA = a.displayOrder?.[primaryWc] ?? a.displayOrder?.[fallbackWc] ?? 999;
          const orderB = b.displayOrder?.[primaryWc] ?? b.displayOrder?.[fallbackWc] ?? 999;
          return orderA - orderB;
        })
    : [];

  // Build a set of operation codes that exist in the order for the CURRENT work center(s)
  // This ensures we only show operations that are actually in the order's routing for THIS work center
  const orderOpsForCurrentWc = new Set();
  effectiveWorkCenters.forEach((wc) => {
    const opsForWc = orderOperationsByWc[wc] || [];
    opsForWc.forEach((opCode) => orderOpsForCurrentWc.add(opCode));
  });

  // Always include REVISION and REWORK operations (they may not be in routing but are always available)
  const alwaysAvailableOps = ["REVISION", "REWORK"];

  // If showAllOperations is true, show all operations for the work center (pending quote mode)
  // Otherwise, filter to order-specific operations
  let filteredOps = baseFilteredOps;
  if (showAllOperations) {
    // In pending quote mode, show all operations for the work center
    // If work center is set, show operations for that work center
    // If no work center, show ALL operations so user can pick any
    if (effectiveWorkCenters.length > 0 && baseFilteredOps.length > 0) {
      filteredOps = baseFilteredOps;
    } else {
      // No work center selected or no ops for work center - show all operations
      filteredOps = operations;
    }
  } else if (orderOpsForCurrentWc.size > 0) {
    const matchedOps = baseFilteredOps.filter((o) =>
      orderOpsForCurrentWc.has(o.operationCode) || alwaysAvailableOps.includes(o.operationCode)
    );
    // Only use matched ops if we found any, otherwise fall back to base
    if (matchedOps.length > 0) {
      filteredOps = matchedOps;
    }
  }

  //
  // CLICK OPERATION
  //
  const handleOperationSelect = (op) => {
    if (!op) return;

    setSelectedOpCode(op.operationCode);

    // For combined work centers, determine actual work center from the operation
    // Use the first work center the operation belongs to
    let actualWorkCenter = workCenter;
    if (workCenter === "ASSEMBLY_OPTI" && op.workCenters) {
      if (op.workCenters.includes("ASSEMBLY")) {
        actualWorkCenter = "ASSEMBLY";
      } else if (op.workCenters.includes("OPTI")) {
        actualWorkCenter = "OPTI";
      }
    } else if (workCenter === "3DMODEL_PAINT" && op.workCenters) {
      if (op.workCenters.includes("3D MODEL")) {
        actualWorkCenter = "3D MODEL";
      } else if (op.workCenters.includes("PAINT")) {
        actualWorkCenter = "PAINT";
      }
    }

    onSelect({
      operationName: op.operationName,
      operationCode: op.operationCode,
      workCenter: actualWorkCenter,
      numberStep: op.numberStep,
    });
  };

  //
  // RENDER
  //
  return (
    <div className="flex flex-col w-full h-[200px] text-gray-900">
      <div className="flex-none mb-2">
        <label className="text-sm font-semibold">Operations</label>
      </div>

      {!order && !showAllOperations && (
        <div className="text-xs text-gray-500">
          Select an order first to see operations.
        </div>
      )}

      {(order || showAllOperations) && (
        <div className="flex flex-col flex-1 min-h-0">
          {error && <div className="text-xs text-red-500 mb-2">{error}</div>}

          {/* OPERATIONS LIST */}
          <div
            className="
              flex-1 overflow-y-auto min-h-0
              bg-slate-200 border border-gray-200 rounded-lg
              divide-y divide-gray-100
              p-1
            "
          >
            {loading && (
              <div className="p-2 text-xs text-gray-500">
                Loading operations…
              </div>
            )}

            {!loading && filteredOps.length === 0 && (
              <div className="p-2 text-xs text-gray-500">
                {showAllOperations
                  ? "No operations available. Loading..."
                  : "No operations for this work center."}
              </div>
            )}

            {filteredOps.map((op) => {
              const isSelected = selectedOpCode === op.operationCode;

              let rowClass = "";
              if (isSelected) {
                rowClass = "bg-orange-50 border-l-2 border-orange-500";
              }

              return (
                <label
                  key={`${op.operationCode}-${op.numberStep}`}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 cursor-pointer
                    hover:bg-slate-100 transition rounded
                    min-h-[44px]
                    ${rowClass}
                  `}
                >
                  <input
                    type="radio"
                    name="operation"
                    checked={isSelected}
                    onChange={() => handleOperationSelect(op)}
                    className="w-5 h-5 flex-shrink-0 accent-purple-600"
                  />

                  <span className="font-medium text-sm md:text-base text-gray-900">
                    {op.operationCode}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
